import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { canView } from "@/lib/permissions";
import { formatPersonName } from "@/lib/person-name";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { PatronDetailSummary } from "@/components/patrons/PatronDetailSummary";
import { PatronProfileCard } from "@/components/patrons/PatronProfileCard";
import { PatronAccessCard } from "@/components/patrons/PatronAccessCard";
import { PatronMessageCard } from "@/components/patrons/PatronMessageCard";
import { PatronAuditCard } from "@/components/patrons/PatronAuditCard";
import { usePatronDetail } from "@/hooks/usePatronDetail";
import { User } from "lucide-react";

export default function PatronDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isPriv = user?.isSuperAdmin || false;
  const can = (p: string) => isPriv || canView(p, perms);
  const canAudit = can("patron.viewHistory") || can("reports.viewPatronLogs");
  const canEdit = can("patron.edit");
  const canMessage = can("notifications.send") || canEdit;

  const state = usePatronDetail(userId, canAudit);

  if (state.loading || !state.detail) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  const { detail } = state;
  const displayName = formatPersonName(detail.firstName, detail.lastName, detail.email);
  const suspended = detail.patronAccountStatus === "SUSPENDED";
  const showAccess = canEdit || can("patron.suspend") || can("patron.activate");

  return (
    <PageContainer>
      <PageHeader
        title={displayName}
        description="Manage profile, borrowing access, and messages for this patron."
        breadcrumbItems={[{ label: "Patrons", href: "/patrons" }, { label: displayName }]}
        showSearch={false}
        titleSize="md"
        actions={
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => navigate("/circulation", { state: { patronLookup: detail.email } })}
          >
            <User className="h-4 w-4" />
            Open at desk
          </Button>
        }
      />

      <PatronDetailSummary detail={detail} />

      <div className="grid gap-6 lg:grid-cols-2">
        {canEdit && (
          <PatronProfileCard
            email={detail.email}
            firstName={state.firstName}
            lastName={state.lastName}
            phone={state.phone}
            saving={state.savingProfile}
            onFirstNameChange={state.setFirstName}
            onLastNameChange={state.setLastName}
            onPhoneChange={state.setPhone}
            onSave={state.saveProfile}
          />
        )}

        {showAccess && (
          <PatronAccessCard
            suspended={suspended}
            canSuspend={can("patron.suspend")}
            canActivate={can("patron.activate")}
            canEdit={canEdit}
            autoRenewSelect={state.autoRenewSelect}
            actionLoading={state.actionLoading}
            onAutoRenewChange={state.setAutoRenewSelect}
            onSaveAutoRenew={state.saveAutoRenew}
            onSuspend={state.doSuspend}
            onActivate={state.doActivate}
          />
        )}

        {canMessage && (
          <PatronMessageCard
            subject={state.msgSubject}
            body={state.msgBody}
            dualChannel={state.msgDual}
            sending={state.sendingMsg}
            onSubjectChange={state.setMsgSubject}
            onBodyChange={state.setMsgBody}
            onDualChannelChange={state.setMsgDual}
            onSend={state.sendMessage}
          />
        )}
      </div>

      {canAudit && <PatronAuditCard rows={state.audit} loading={state.auditLoading} />}
    </PageContainer>
  );
}
